/**
 * Case Questions lane (#613, ADR-0150) — the Firm↔Firm tab of Operations →
 * Messages. Every typed question→answer pair on a placement, unanswered-first
 * so the demands lead. CC sees the ADMIN shape: real names (unlike the firm-
 * facing role-only embed). A question is a single Q→A pair, not a thread, so the
 * row renders the whole thing inline — there is nothing to expand.
 *
 * Search is as-you-type, client-side, over the honest text: client, firm names,
 * asker/answerer names, the question, the note, and the answer. The corpus is
 * fetch-all (Messages-lane posture), so narrowing needs no server round trip.
 */

import { useMemo, useState } from "react";
import type { CaseQuestion } from "@command-center/shared";
import { Input } from "@/components/ui/input";
import { dayZone } from "@/lib/format";

/** Unanswered-first, then most-recent activity — Open by asked_at desc, Answered
 *  by answered_at desc. The demands lead; a settled answer never buries an open
 *  question. */
export function sortCaseQuestions(questions: CaseQuestion[]): CaseQuestion[] {
  return questions.slice().sort((a, b) => {
    const openRank = Number(a.status === "Answered") - Number(b.status === "Answered");
    if (openRank !== 0) return openRank;
    const aStamp = a.status === "Answered" ? a.answered_at : a.asked_at;
    const bStamp = b.status === "Answered" ? b.answered_at : b.asked_at;
    return bStamp.localeCompare(aStamp);
  });
}

/** As-you-type search over the honest text. Case-insensitive substring.
 *  Exported for the unit test. */
export function matchesCaseQuestion(q: CaseQuestion, query: string): boolean {
  const s = query.trim().toLowerCase();
  if (s === "") return true;
  return [
    q.case_label,
    q.of_firm_name,
    q.hf_firm_name,
    q.acting_name,
    q.answered_by_name,
    q.question,
    q.note,
    q.answer,
  ].some((f) => f.toLowerCase().includes(s));
}

export function CaseQuestionsLane({
  questions,
  isLoading,
  isError,
}: {
  questions: CaseQuestion[];
  isLoading: boolean;
  isError: boolean;
}) {
  const [query, setQuery] = useState("");

  const shown = useMemo(
    () => sortCaseQuestions(questions.filter((q) => matchesCaseQuestion(q, query))),
    [questions, query],
  );
  const openCount = questions.filter((q) => q.status === "Open").length;

  if (isLoading) {
    return <p className="p-4 text-sm text-plane-muted">Loading questions…</p>;
  }
  if (isError) {
    return (
      <p className="p-4 text-sm text-plane-muted" data-testid="case-questions-error">
        Couldn't load case questions. Refresh to retry.
      </p>
    );
  }

  return (
    <div data-testid="case-questions-lane">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search client, firm, name, or question text…"
          aria-label="Search case questions"
          data-testid="case-questions-search"
          className="max-w-sm"
        />
        <span className="text-[11px] uppercase tracking-wider text-plane-muted" data-testid="case-questions-count">
          {questions.length} total{openCount > 0 ? ` · ${openCount} awaiting answer` : ""}
        </span>
      </div>

      {questions.length === 0 ? (
        <p className="text-sm italic text-plane-muted" data-testid="case-questions-empty">
          No case questions yet. Firms and LegalFlare ask handling firms from a placed case.
        </p>
      ) : shown.length === 0 ? (
        <p className="text-sm italic text-plane-muted" data-testid="case-questions-empty-filtered">
          Nothing matches. Clear the search to see every question.
        </p>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="case-questions-list">
          {shown.map((q) => (
            <CaseQuestionRow key={q.id} q={q} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CaseQuestionRow({ q }: { q: CaseQuestion }) {
  const answered = q.status === "Answered";
  // CC sees the ADMIN shape — show the real asker (the contact who asked), same
  // as the case-detail panel row; fall back to firm name then a role label.
  const asker =
    q.asker_role === "LF" ? "LegalFlare" : q.acting_name || q.of_firm_name || "Originating firm";
  const askedStamp = q.asked_at ? fmtDate(q.asked_at) : "";
  return (
    <li
      className="rounded-md border border-border bg-card p-3"
      data-testid="case-question-row"
      data-status={q.status}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{q.case_label || "—"}</span>
        <StatusChip answered={answered} />
      </div>
      <div className="mb-2 text-[11px] text-plane-muted">
        {q.of_firm_name || "—"} → {q.hf_firm_name || "—"}
      </div>
      <div className="text-sm">
        <span className="font-medium">{asker}</span>
        <span className="text-plane-muted"> asked{askedStamp ? ` · ${askedStamp}` : ""}</span>
        <div>{q.question}</div>
        {q.note ? <div className="mt-0.5 text-plane-muted">“{q.note}”</div> : null}
      </div>
      {answered ? (
        <div className="mt-2 border-l-2 border-active-edge/40 pl-2 text-sm">
          <span className="font-medium">{q.answered_by_name || q.hf_firm_name || "Handling firm"}</span>
          <span className="text-plane-muted">
            {" "}
            answered{q.answered_at ? ` · ${fmtDate(q.answered_at)}` : ""}
          </span>
          <div>{q.answer}</div>
        </div>
      ) : (
        <div className="mt-2 text-xs italic text-plane-muted">Awaiting the handling firm's answer.</div>
      )}
    </li>
  );
}

function StatusChip({ answered }: { answered: boolean }) {
  return (
    <span
      className={
        answered
          ? "rounded-full bg-plane-muted/15 px-2 py-0.5 text-[10px] font-semibold text-plane-muted"
          : "rounded-full bg-active-edge/15 px-2 py-0.5 text-[10px] font-semibold text-active-fg"
      }
    >
      {answered ? "Answered" : "Awaiting answer"}
    </span>
  );
}

/** Date-only, locale-formatted. Kept local — the lane has no other date needs. */
function fmtDate(iso: string): string {
  const z = dayZone(iso);
  return z === null
    ? iso
    : new Date(z.ms).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: z.timeZone,
      });
}
